import { Database, KeyRound, LayoutGrid, MapPin } from 'lucide-react';
import React from 'react';
import { CopyButton } from '../components/CopyButton';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';

type SettingsPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function SettingsPage({ openDialog }: SettingsPageProps) {
  const {
    workspace,
    activeOrganization,
  } = useWorkspace();

  const { apps, environments, apiKeys } = workspace;

  if (!activeOrganization) {
    return null;
  }

  return (
    <div className="workspaceSettingsPage">
      <SectionCard
        className="workspaceProfilePanel"
        label="Workspace identity"
        title={activeOrganization.name}
        description="The stable workspace boundary shared by projects, people and integrations."
      >
        <div className="workspaceProfileGrid">
          <div>
            <span className="workspaceProfileIcon"><LayoutGrid size={16} /></span>
            <span><small>Workspace slug</small><strong><code>{activeOrganization.slug}</code><CopyButton value={activeOrganization.slug} label="Copy workspace slug" /></strong></span>
          </div>
          <div>
            <span className="workspaceProfileIcon"><MapPin size={16} /></span>
            <span><small>Region</small><strong>{activeOrganization.subtitle.split('·')[0]?.trim() || 'Not recorded'}</strong></span>
          </div>
          <div>
            <span className="workspaceProfileIcon"><Database size={16} /></span>
            <span><small>Projects and environments</small><strong>{apps.length} projects · {environments.length} environments</strong></span>
          </div>
          <div>
            <span className="workspaceProfileIcon"><KeyRound size={16} /></span>
            <span><small>Active API keys</small><strong>{apiKeys.filter((key) => key.isActive).length}</strong></span>
          </div>
        </div>
      </SectionCard>

      <div className="workspaceDangerZone">
        <SectionCard
          className="dangerZonePanel"
          label="Danger zone"
          title="Delete workspace"
          description="Permanently deactivates this workspace and every project, membership, release, runtime rule and API key it owns. Only an owner can continue."
        >
          <div className="dangerZoneAction">
            <p>This cannot be undone from the admin UI.</p>
            <button
              type="button"
              className="dangerButton"
              onClick={() =>
                openDialog({
                  kind: 'delete-organization',
                  organizationId: activeOrganization.id,
                  organizationName: activeOrganization.name
                })
              }
            >
              Delete workspace
            </button>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

export { SettingsPage };
